import type { WebVitalsOptions, WebVitalsParams } from '@loyalty/utils';

import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, NgZone, PLATFORM_ID } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconRegistry } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { RouterOutlet } from '@angular/router';
import { WzbBoNotificationsComponent } from '@commons/bo-ui';
import { InterserviceNavbarComponent } from '@commons/interservice-navbar/interservice-navbar.component';
import { NavigationMenuComponent } from '@commons/navigation-menu/navigation-menu.component';
import { VersionComponent } from '@commons/version/version.component';
import { environment } from '@env/environment';
import { sendWebVitals } from '@loyalty/utils';
import { Store } from '@ngrx/store';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { selectCurrentRoute, selectUrl } from '@stores/router/router.selectors';
import { combineLatest } from 'rxjs';

@Component({
  selector: 'app-core',
  imports: [
    RouterOutlet,
    MatProgressBarModule,
    NavigationMenuComponent,
    VersionComponent,
    InterserviceNavbarComponent,
    WzbBoNotificationsComponent,
  ],
  template: `
    <app-notifications />

    <app-interservice-navbar class="interservice-navbar" />
    <app-version class="version" />

    <div class="progress-bar-wrapper">
      @if (showLoadingBar()) {
        <mat-progress-bar mode="indeterminate" />
      }
    </div>

    <main class="main">
      <app-navigation-menu class="aside" />

      <section class="section">
        <router-outlet />
      </section>
    </main>
  `,
  styles: [
    `
      .main {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr);
      }

      .progress-bar-wrapper {
        height: 0.25rem;
        position: fixed;
        top: 0;
        width: 100dvw;
        z-index: 1000;
      }

      .aside {
        position: sticky;
        top: 0;
        height: 100svh;
        z-index: 3;
      }

      .version {
        display: block;
        position: fixed;
        bottom: 135px;
        left: 0;
        z-index: 1000;
        width: 100px;
        padding: 0;
        text-align: center;
      }

      /* Warning: keep z-index higher than header in builder-layout component */
      .interservice-navbar {
        position: fixed;
        bottom: 1.25rem;
        left: 2.375rem;
        z-index: 4;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoreComponent {
  readonly #store = inject(Store);
  readonly #platformId = inject(PLATFORM_ID);
  readonly #ngZone = inject(NgZone);
  readonly #matIconRegistry = inject(MatIconRegistry);
  readonly #domSanitizer = inject(DomSanitizer);
  readonly showLoadingBar = toSignal(inject(LoadingBarService).value$);

  constructor() {
    this.#setIconRegistry();
    this.#webVitals();
  }

  readonly #setIconRegistry = (): void => {
    this.#ngZone.runOutsideAngular(() =>
      this.#matIconRegistry.addSvgIconSet(
        this.#domSanitizer.bypassSecurityTrustResourceUrl(`${environment.deployAssetsUrl}/sprites/icons.svg`)
      )
    );
  };

  readonly #webVitals = (): void => {
    combineLatest([this.#store.select(selectCurrentRoute), this.#store.select(selectUrl)]).subscribe(
      ([routerParams, url]: [routerParams: Record<string, string>[], url: string]) => {
        if (url) {
          const params: WebVitalsParams = {
            params: routerParams,
            path: url,
            applicationId: environment.applicationId,
            envFqdn: environment.domain,
          };
          const options: WebVitalsOptions = {
            dev: environment.platform === 'local',
            debug: environment.platform === 'local',
            browser: isPlatformBrowser(this.#platformId),
          };
          sendWebVitals(params, options);
        }
      }
    );
  };
}
